<script setup>
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useI18n } from 'vue-i18n';

defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
  primaryContactId: {
    type: [Number, null],
    default: null,
  },
  primaryContactList: {
    type: Array,
    default: () => [],
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:primaryContactId', 'search']);

const { t } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex flex-col gap-3 rounded-xl border border-auth-border bg-auth-surface p-4 dark:border-auth-border-dark dark:bg-auth-surface-dark"
    >
      <div class="flex h-5 items-center justify-between gap-2">
        <label
          class="text-sm font-medium text-auth-text dark:text-auth-text-dark"
        >
          {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PRIMARY') }}
        </label>
        <span
          class="flex h-5 w-24 items-center justify-center rounded-md bg-auth-action/10 text-xs font-medium text-auth-action dark:bg-auth-accent/10 dark:text-auth-accent"
        >
          {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PRIMARY_HELP_LABEL') }}
        </span>
      </div>
      <ComboBox
        id="inbox"
        use-api-results
        :model-value="primaryContactId"
        :options="primaryContactList"
        :empty-state="
          isSearching
            ? t('CONTACTS_LAYOUT.SIDEBAR.MERGE.IS_SEARCHING')
            : t('CONTACTS_LAYOUT.SIDEBAR.MERGE.EMPTY_STATE')
        "
        :search-placeholder="
          t('CONTACTS_LAYOUT.SIDEBAR.MERGE.SEARCH_PLACEHOLDER')
        "
        :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PLACEHOLDER')"
        :has-error="hasError"
        :message="errorMessage"
        class="[&>div>button]:!h-10 [&>div>button]:!rounded-xl [&>div>button]:!bg-auth-soft [&>div>button]:!text-auth-text [&>div>button]:!outline-auth-border dark:[&>div>button]:!bg-auth-soft-dark dark:[&>div>button]:!text-auth-text-dark dark:[&>div>button]:!outline-auth-border-dark"
        @update:model-value="value => emit('update:primaryContactId', value)"
        @search="query => emit('search', query)"
      />
    </div>
    <div class="relative flex justify-center gap-2">
      <div v-for="i in 3" :key="i" class="relative h-6 w-4">
        <div
          class="absolute start-1 top-0 h-0 w-0 border-b-[0.375rem] border-s-[0.25rem] border-e-[0.25rem] border-b-auth-action border-s-transparent border-e-transparent dark:border-b-auth-accent"
        />
        <div
          class="absolute start-1/2 top-1 h-full w-px -translate-x-1/2 bg-auth-border dark:bg-auth-border-dark"
        />
      </div>
    </div>
    <div
      class="flex flex-col gap-3 rounded-xl border border-n-ruby-6 bg-n-ruby-2/40 p-4 dark:border-n-ruby-7 dark:bg-n-ruby-3/10"
    >
      <div class="flex h-5 items-center justify-between gap-2">
        <label
          class="text-sm font-medium text-auth-text dark:text-auth-text-dark"
        >
          {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PARENT') }}
        </label>
        <span
          class="flex h-5 w-24 items-center justify-center rounded-md bg-n-ruby-3 text-xs font-medium text-n-ruby-11 dark:bg-n-ruby-4/20 dark:text-n-ruby-10"
        >
          {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PARENT_HELP_LABEL') }}
        </span>
      </div>
      <div
        class="flex h-[3.75rem] items-center gap-3 rounded-lg border border-n-ruby-6 bg-auth-surface p-3 dark:border-n-ruby-7 dark:bg-auth-surface-dark"
      >
        <Avatar
          :name="selectedContact.name || ''"
          :src="selectedContact.thumbnail || ''"
          :size="32"
          rounded-full
        />
        <div class="flex w-full min-w-0 flex-col gap-1">
          <span
            class="truncate text-sm font-medium leading-4 text-auth-text dark:text-auth-text-dark"
          >
            {{ selectedContact.name }}
          </span>
          <span
            class="truncate text-xs leading-4 text-auth-muted dark:text-auth-muted-dark"
          >
            {{ selectedContact.email }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
